import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EventsService } from '~/services/domain/EventsService';
import { useToastMessages } from '@/hooks/useToastMessages';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { StarIcon as StarSolidIcon } from '@hugeicons-pro/core-solid-rounded';
import { StarIcon as StarStrokeIcon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';

interface EventFollowButtonProps {
  eventId: number;
  isFollowing: boolean;
  userName: string;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  onFollowingChange?: (isFollowing: boolean) => void;
}

export function EventFollowButton({
  eventId,
  isFollowing: initialIsFollowing,
  userName,
  className,
  size = 'sm',
  onFollowingChange,
}: EventFollowButtonProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);

  useEffect(() => {
    setIsFollowing(initialIsFollowing);
  }, [initialIsFollowing]);

  const followMutation = useMutation({
    mutationFn: () => EventsService.followEvent(eventId),
    onMutate: () => {
      setIsFollowing(true);
      onFollowingChange?.(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-profile', userName] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (error) => {
      setIsFollowing(false);
      onFollowingChange?.(false);
      showErrorToast(t('events.followError'), error);
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: () => EventsService.unfollowEvent(eventId),
    onMutate: () => {
      setIsFollowing(false);
      onFollowingChange?.(false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-profile', userName] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (error) => {
      setIsFollowing(true);
      onFollowingChange?.(true);
      showErrorToast(t('events.unfollowError'), error);
    },
  });

  const handlePress = () => {
    if (isFollowing) {
      unfollowMutation.mutate();
    } else {
      followMutation.mutate();
    }
  };

  const isPending = followMutation.isPending || unfollowMutation.isPending;

  return (
    <ButtonWrapper
      size={size}
      variant="solid"
      action={isFollowing ? 'neutral' : 'primary'}
      icon={isFollowing ? StarSolidIcon : StarStrokeIcon}
      iconClassName={isFollowing ? 'color-yellow-500' : ''}
      iconSize={18}
      text={isFollowing ? t('events.following') : t('events.follow')}
      onPress={handlePress}
      isDisabled={isPending}
      className={className}
    />
  );
}
